import { gql, useQuery } from '@apollo/react-hooks';
import dayjs from 'dayjs';
import type {
  PrioritizeEvaluationData,
  GetPrioritizeEvaluationData,
} from './types';
import {
  FundingPanel,
  type FundingRound,
} from '../../../../Nomination/components';
import { PAGE_TEXT } from '../../../../Nomination/components/lang';
import { checkInvalidDate } from '../../../../Application/graphql';
import {
  modeOfStudyOptions,
  programmeTypeOptions,
} from '../../../../Application/components/ApplicationForm/ProgrammeChoice/options';
import { getOptionsLabel } from '../../../../Monitor/components';

const fundingSourceValues = PAGE_TEXT.FUNDING_SOURCE.FUNDING_SCHEME.VALUES;
const GET_SCHOLARSHIP_PRIORITY = gql`
  query (
    $funding: FUNDING_SOURCE!
    $unitName: String
    $academicYear: String
    $round: FUNDING_ROUND
  ) {
    scholarshipPriority(
      input: {
        funding: $funding
        unitName: $unitName
        academicYear: $academicYear
        round: $round
      }
    ) {
      applications {
        id
        applicationNo
        updatedAt
        applicant {
          name {
            givenName
            surname
          }
        }
        programmeChoice {
          department
          programmeType
          researchArea
          entryYear
          proposedChiefSupervisor
        }

        nomination {
          supervisors {
            type
            netId
            surname
            givenName
          }
          scholarshipNomination {
            subjectClassification
            scores {
              funding
              score
            }
          }
        }
      }
      order {
        academicYear
        fundingOrder {
          applicationId
          applicationNo
          order
        }
        submitted
        submittedAt
      }
    }
    getFundingSourceConfiguration {
      mapping
    }
  }
`;

const NA = 'N/A';
export const useGetScholarshipPriorityListPageData = (
  academicYear: string,
  round: FundingRound,
  funding: string,
  department: string,
): GetPrioritizeEvaluationData => {
  const { data, loading, error, refetch } = useQuery(GET_SCHOLARSHIP_PRIORITY, {
    variables: { academicYear, round, funding, unitName: department },
    fetchPolicy: 'network-only',
    skip: !funding,
  });

  const scholarshipOrdering =
    data?.scholarshipPriority?.order?.fundingOrder ?? [];

  // Step 1: Create a mapping for the scholarshipOrdering
  const orderMapping = scholarshipOrdering.reduce((acc: any, curr: any) => {
    acc[curr?.applicationId] = curr?.order ?? -1;
    return acc;
  }, {});

  const fundingPanelTextDisplay: Partial<Record<FundingPanel, string>> = {
    [FundingPanel.SCIENCES_MEDICINE_ENGINEERING_TECHNOLOGY]: 'S',
    [FundingPanel.HUMANITIES_SOCIAL_SCIENCES_BUSINESS_STUDIES]: 'H',
  };

  // Step 2: Assign order values to applicationViewsPaginated then sort
  const prioritizeEvaluationData: PrioritizeEvaluationData[] = (
    data?.scholarshipPriority?.applications ?? []
  )
    .map((application: any) => {
      const {
        id,
        applicationNo,
        applicant,
        programmeChoice,
        nomination,
        updatedAt,
      } = application;

      const programmeType = getOptionsLabel(
        programmeTypeOptions,
        programmeChoice?.programmeType,
      );

      const studyMode = getOptionsLabel(
        modeOfStudyOptions,
        programmeChoice?.studyMode,
      );

      // get confirmed supervisor
      const confirmedChiefSupervisorItem = nomination?.supervisors?.find(
        (item: any) => {
          // TODO map type to defined constant
          return item.type === 'CHIEF_SUPERVISOR';
        },
      );
      const {
        surname: supervisorSurname,
        givenName: supervisorGivenName,
        netId,
      } = confirmedChiefSupervisorItem ?? {};
      const confirmedChiefSupervisor = (confirmedChiefSupervisorItem as boolean)
        ? `${supervisorSurname} ${supervisorGivenName} (${netId})`
        : NA;

      return {
        applicationId: id ?? NA,
        applicationNo: applicationNo ?? NA,
        applicantName: `${applicant?.name?.givenName ?? ''} ${applicant?.name?.surname ?? ''}`,
        department: programmeChoice?.department ?? NA,
        researchField: programmeChoice?.researchArea ?? NA,
        progType: programmeType ?? NA,
        studyMode: studyMode ?? NA,
        chiefSupervisor: confirmedChiefSupervisor,
        fundingScheme:
          fundingSourceValues[funding as keyof typeof fundingSourceValues],
        subjectClassification:
          fundingPanelTextDisplay[
            nomination.scholarshipNomination
              ?.subjectClassification as FundingPanel
          ] ?? NA,
        fundingScore:
          nomination.scholarshipNomination?.scores
            ?.find((item: any) => item?.funding === funding)
            ?.score?.toFixed(2) ?? NA,
        submissionTime: checkInvalidDate(updatedAt)
          ? NA
          : dayjs(updatedAt).format('DD/MM/YYYY'),
        entryYear: programmeChoice?.entryYear ?? NA,
        order: orderMapping[id] ?? -1,
      };
    })
    .sort((a: any, b: any) => a.order - b.order)
    .map((item: any, index: number) => ({
      ...item,
      index, // used for formik array
      order: item.order > 0 ? index + 1 : '-', // default value '-' for no ordering
    }));

  const competitiveFunding =
    data?.getFundingSourceConfiguration?.mapping?.COMPETITIVE ?? [];

  const submitted = data?.scholarshipPriority?.order?.submitted ?? false;
  const submittedAt = data?.scholarshipPriority?.order?.submittedAt ?? false;
  const deadline =
    competitiveFunding.find((item: any) => item?.value === funding)
      ?.orderingDeadline ?? null;
  const priorityFundingInfo = {
    submitted,
    submittedAt: checkInvalidDate(submittedAt)
      ? NA
      : dayjs(submittedAt).format('DD/MM/YYYY HH:mm:ss'),
    deadline,
  };

  return {
    prioritizeEvaluationData,
    priorityFundingInfo,
    loading,
    error,
    refetch,
  };
};
